/**
 * Prize List Component - displays and manages prizes
 */

import { useState, useCallback, useEffect } from "react";
import {
  IconGift,
  IconTrash,
  IconCheck,
  IconChevronUp,
  IconChevronDown,
  IconPencil,
} from "@/components/Icons";
import { haptic } from "@/lib/telegram";
import type { Prize } from "../hooks";

interface PrizeListProps {
  prizes: Prize[];
  canEdit: boolean;
  onDelete: (prizeId: string) => void;
  onReorder: (prizeIds: string[]) => Promise<void>;
  onUpdate?: (args: { id: string; name: string; description?: string }) => Promise<void>;
}

export function PrizeList({
  prizes: initialPrizes,
  canEdit,
  onDelete,
  onReorder,
  onUpdate,
}: PrizeListProps) {
  // Local state for optimistic UI updates
  const [localPrizes, setLocalPrizes] = useState(initialPrizes);
  const [isReordering, setIsReordering] = useState(false);
  const [editingPrizeId, setEditingPrizeId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state when props change (e.g., after add/delete)
  useEffect(() => {
    setLocalPrizes(initialPrizes);
  }, [initialPrizes]);

  const startEditing = useCallback((prize: Prize) => {
    setEditingPrizeId(prize.id);
    setEditName(prize.name);
    setEditDescription(prize.description ?? "");
    haptic("light");
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingPrizeId(null);
    setEditName("");
    setEditDescription("");
  }, []);

  const saveEditing = useCallback(async () => {
    if (!editingPrizeId || !onUpdate || !editName.trim()) return;

    setIsSaving(true);
    try {
      await onUpdate({
        id: editingPrizeId,
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      haptic("success");
      cancelEditing();
    } catch {
      haptic("error");
    } finally {
      setIsSaving(false);
    }
  }, [editingPrizeId, editName, editDescription, onUpdate, cancelEditing]);

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const newPrizes = [...localPrizes];
    const temp = newPrizes[index];
    newPrizes[index] = newPrizes[index - 1]!;
    newPrizes[index - 1] = temp!;

    // Optimistic update - immediately show new order
    setLocalPrizes(newPrizes);
    setIsReordering(true);
    haptic("light");

    try {
      await onReorder(newPrizes.map((p) => p.id));
    } catch {
      // Revert on error
      setLocalPrizes(localPrizes);
    } finally {
      setIsReordering(false);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === localPrizes.length - 1) return;

    const newPrizes = [...localPrizes];
    const temp = newPrizes[index];
    newPrizes[index] = newPrizes[index + 1]!;
    newPrizes[index + 1] = temp!;

    // Optimistic update - immediately show new order
    setLocalPrizes(newPrizes);
    setIsReordering(true);
    haptic("light");

    try {
      await onReorder(newPrizes.map((p) => p.id));
    } catch {
      // Revert on error
      setLocalPrizes(localPrizes);
    } finally {
      setIsReordering(false);
    }
  };

  if (localPrizes.length === 0) {
    return (
      <div className="card p-6 text-center">
        <IconGift className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p className="text-sm text-slate-500">Добавьте призы для розыгрыша</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {localPrizes.map((prize, index) => {
        const isEditing = editingPrizeId === prize.id;

        return (
          <div
            key={prize.id}
            className={`card p-4 ${prize.draw ? "opacity-50" : ""}`}
          >
            {isEditing ? (
              /* Edit mode */
              <div className="space-y-3">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Название приза"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Описание (необязательно)"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <button
                    onClick={saveEditing}
                    disabled={isSaving || !editName.trim()}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
                  >
                    {isSaving ? "Сохранение..." : "Сохранить"}
                  </button>
                  <button
                    onClick={cancelEditing}
                    disabled={isSaving}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              /* View mode */
              <div className="flex items-center gap-3">
                {/* Order number */}
                <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </div>

                {/* Prize image or icon */}
                {prize.imageUrl ? (
                  <img
                    src={prize.imageUrl}
                    alt={prize.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <IconGift className="w-6 h-6 text-slate-400" />
                  </div>
                )}

                {/* Prize info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-slate-900 truncate">
                    {prize.name}
                  </h3>
                  {prize.description && (
                    <p className="text-sm text-slate-500 truncate">
                      {prize.description}
                    </p>
                  )}
                </div>

                {/* Status badge */}
                {prize.draw && (
                  <span className="px-2 py-1 rounded-lg bg-green-100 text-green-600 text-xs font-medium flex items-center gap-1">
                    <IconCheck className="w-3 h-3" />
                    Разыгран
                  </span>
                )}

                {/* Actions */}
                {canEdit && !prize.draw && (
                  <div className="flex items-center gap-1">
                    {onUpdate && (
                      <button
                        onClick={() => startEditing(prize)}
                        className="p-2 rounded-lg hover:bg-slate-100"
                      >
                        <IconPencil className="w-4 h-4 text-slate-500" />
                      </button>
                    )}
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0 || isReordering}
                      className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"
                    >
                      <IconChevronUp className="w-4 h-4 text-slate-500" />
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === localPrizes.length - 1 || isReordering}
                      className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-30"
                    >
                      <IconChevronDown className="w-4 h-4 text-slate-500" />
                    </button>
                    <button
                      onClick={() => onDelete(prize.id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                    >
                      <IconTrash className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
